import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Ship, Check } from "lucide-react";
import { API, formatError } from "@/lib/api";
import { STAGES } from "@/lib/stages";

const StageRow = ({ s, last }) => {
  const Icon = STAGES.find((x) => x.key === s.key)?.icon || Ship;
  const tone = s.state === "done" ? "bg-emerald-600/80 border-emerald-400"
    : s.state === "current" ? "bg-blue-600 border-blue-300 stepper-dot-active" : "bg-slate-800 border-slate-700";
  return (
    <li data-testid={`track-stage-${s.key}`} className="relative flex gap-4 pb-6">
      {!last && <div className={`absolute left-5 top-10 bottom-0 w-[2px] ${s.state === "done" ? "bg-emerald-500/60" : "bg-slate-800"}`} />}
      <div className={`relative z-10 shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${tone}`}>
        {s.state === "done" ? <Check size={16} /> : <Icon size={16} />}
      </div>
      <div className="pt-1.5">
        <div className={`text-sm font-medium ${s.state === "upcoming" ? "text-slate-500" : "text-slate-100"}`}>{s.label}</div>
        <div className="text-xs text-slate-500">
          {s.state === "current" ? "In progress" : s.state === "done" ? "Completed" : "Upcoming"}
          {s.reached_at ? ` · ${new Date(s.reached_at).toLocaleDateString()}` : ""}
        </div>
      </div>
    </li>
  );
};

export default function TrackShipment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/track/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatError(e)));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 px-5 py-10">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center"><Ship size={18} /></div>
          <div>
            <div className="font-display font-bold tracking-wide leading-tight">LEAD ASIA CARGO</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Shipment status</div>
          </div>
        </div>
        {error && <div data-testid="track-error" className="glass-card rounded-lg p-6 text-sm text-rose-300">{error}</div>}
        {!data && !error && <div className="text-slate-400">Loading…</div>}
        {data && (
          <div className="glass-card rounded-lg p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Shipment</div>
            <h1 data-testid="track-shipment-number" className="font-display text-3xl sm:text-4xl font-bold font-mono-ops break-all">{data.shipment_number}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span data-testid="track-current-stage" className="px-3 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-sky-300 text-xs uppercase tracking-widest">
                {data.current_stage_label}
              </span>
              <span data-testid="track-updated-at" className="text-xs text-slate-500">
                Last update {data.updated_at ? new Date(data.updated_at).toLocaleString() : "—"}
              </span>
            </div>
            <ol className="mt-8">
              {data.stages.map((s, i) => <StageRow key={s.key} s={s} last={i === data.stages.length - 1} />)}
            </ol>
          </div>
        )}
        <p className="text-xs text-slate-600 mt-6">For questions about this shipment, please contact your Lead Asia Cargo representative.</p>
      </div>
    </div>
  );
}
